(ns manage.variants-preview-panel.variant-id-table.view
  (:require ["@mui/material/Chip$default" :as Chip]
            ["@mui/material/Divider$default" :as Divider]
            ["@mui/material/FormControl$default" :as FormControl]
            ["@mui/material/MenuItem$default" :as MenuItem]
            ["@mui/material/Select$default" :as Select]
            ["@mui/material/Skeleton$default" :as Skeleton]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/Typography$default" :as Typography]
            [clojure.set :as set]
            [clojure.string :as str]
            [common.flags :refer [use-flag]]
            [common.widgets.checkbox :refer [checkbox]]
            [common.widgets.input :refer [input]]
            [common.widgets.scroll :as scroll]
            [common.widgets.table :as t]
            [manage.variants-preview-panel.variant-id-table.db]
            [mate.core :as mc]
            [re-frame.core :as rf]
            [reagent.core :as r]
            [uix.core :refer [$ defui]]
            [uix.re-frame :refer [use-subscribe]]))

(def fields
  [[:selected "" false]
   [:label "Variants" true]])

(def score-filters
  [85
   75
   50
   25
   15])

(def status-filters
  ["draft"
   "approved"
   "published"])

(defui row
  [{:keys [id row-value]}]
  (let [row-selected? (use-subscribe [:table.variant-id/row-selected? id])
        create-lift-feature? (use-flag :create-lift-preview)]
    (r/as-element
     [t/table-row {:selected? row-selected?}
      [t/table-cell {:style {:max-width "5rem"}}
       ($ checkbox
          {:value row-selected?
           :on-change #(if %
                         (rf/dispatch [:table.variant-id/select-row id])
                         (rf/dispatch [:table.variant-id/deselect-row id]))})]
      (for [[key _ show?] fields]
        (when show?
          [t/table-cell {:key key
                         :style {:white-space "normal"
                                 :word-break "break-all"}}
           (get row-value key)]))
      (when create-lift-feature?
        [t/table-cell
         {:style {:max-width "10rem"
                  :justify-content "flex-end"}}
         (if (:score-available row-value)
           [:> Chip
            {:color (if (>= (:score row-value) 50)
                      "success"
                      "error")
             :variant "subtle"
             :label (str (:score row-value) "%")
             :sx {:display "flex"
                  :width "4.5rem"
                  :textAlign "right"}}]
           [:> Skeleton
            {:width "4.5rem"}])])])))

(defn create-status-filters [current-filter new-value]
  (if (nil? current-filter)
    (vector new-value)
    (if (some #(= new-value %) current-filter)
      (remove #(= new-value %) current-filter)
      (conj current-filter new-value))))

(defui table []
  (let [{:keys [value]} (use-subscribe [:table.variant-id/value])
        selected-row-set (use-subscribe [:table.variant-id/selected-rows])
        filtered-row-set (set (map :id value))
        total (count (:value (use-subscribe [:variants-preview.variants/values])))
        selected-state (cond
                         ;; Consider selection empty if none of the filtered items are selected
                         (empty? (set/intersection filtered-row-set selected-row-set)) :none
                         ;; Consider all items selected, if at least all filtered items are selected.
                         ;; Selection could also contain items that are filtered out (not in filtered-row-set).
                         (set/subset? filtered-row-set selected-row-set) :all
                         :else :partial)
        search-term (use-subscribe [:table.variant-id/search-term])
        create-lift-feature? (use-flag :create-lift-preview)
        filters (use-subscribe [:table.variant-id/filters])]
    (r/as-element
     [t/table {:style {:min-height 0}}
      [scroll/infinite-scroll {:parent-id "infinite-scroll-box"
                               :can-show-more? true
                               :load-fn #(rf/dispatch [:table.variant-id/load-more-variants])}]

      [:<>
       [t/table-row
        {:style {:border-bottom-width (if create-lift-feature? 0 1)}}
        [t/table-head
         {:col-span 3
          :style (cond-> {}
                   create-lift-feature? (assoc :padding-bottom 0))}
         ($ input
            {:onChange (fn [e]
                         ;; table init isn't called, workaround
                         (rf/dispatch [:table.variant-id/search-column :label])
                         (rf/dispatch [:table.variant-id/search-term (-> e .-target .-value)])
                         (rf/dispatch [:table.variant-id/confirm-search]))
             :data-testid "search-input"
             :onClick #(-> % .-target .select)
             :placeholder "Search variants"
             :value search-term})]]
       [:> Stack
        {:direction "row"
         :justifyContent "space-between"
         :alignItems "center"
         :px 3
         :py 2}
        [:> FormControl
         {:size "small"}
         [:> Select
          {:id "filter"
           :on-change (fn [e]
                        (let [v (.. e -target -value)
                              new-value (last v)]
                          (if (some #(= new-value %) status-filters)
                            (rf/dispatch [:table.variant-id/filters (assoc filters :status (create-status-filters (:status filters) new-value))])
                            (if (nil? new-value)
                              (rf/dispatch [:table.variant-id/filters (dissoc filters :min-score)])
                              (rf/dispatch [:table.variant-id/filters (assoc filters :min-score new-value)])))))
           :value [(:status filters) (:min-score filters)]
           :displayEmpty true
           :multiple true
           :renderValue (fn [] "Filter")
           :sx {:min-width "15rem"
                :text-transform "none"
                :p 0.5}}
          [:> Typography
           {:sx #js {:color "text.subtle"}
            :font-weight 600
            :padding 1
            :key :status-header}
           "STATUS"]
          ($ Stack {:px 2}
             (for [x status-filters]
               ($ checkbox
                  {:key x
                   :label (str/capitalize x)
                   :value (some #(= x %) (:status filters))
                   :on-change #(rf/dispatch [:table.variant-id/filters (assoc filters :status (create-status-filters (:status filters) x))])})))
          (when create-lift-feature?
            [:<>
             [:> Divider {:sx {:py "1rem"}
                          :key :divider}]
             [:> Typography
              {:sx #js {:color "text.subtle"}
               :font-weight 600
               :padding "1rem"
               :key :score-header}
              "MINIMUM SCORE"]
             (for [x score-filters]
               [:> MenuItem
                {:key x
                 :value x}
                x "%"])
             [:> MenuItem
              {:value nil}
              "none"]])]]
        [:> Typography (str "Viewing " (count value) " / " total)]]]

      ;; Column names row
      [t/table-row {}
       [t/table-head {:style {:max-width "5rem"}}
        ($ checkbox
           {:value (not= selected-state :none)
            :indeterminate (or (= selected-state :partial) false)
            :on-change #(if (= selected-state :all)
                          (rf/dispatch [:table.variant-id/deselect-all-rows])
                          (rf/dispatch [:table.variant-id/select-all-rows]))})]
       (for [[index [_ column-label show?]] (mc/seq-indexed fields)
             :when show?]
         ^{:key index} [t/table-head {} column-label])]

      ;; Data rows
      (for [{:keys [id] :as row-value} value]
        ($ row {:key id :id id :row-value row-value}))])))
